import { CHALLENGE_STAGES, ChallengeStage } from '../constants/challengeConfig';
import { getMonsterById } from '../constants/monsters';
import { EREBOS_MONSTER, EREBOS_FORM2_MONSTER } from '../constants/bossConfig';

/** ステージの状態 */
export enum StageStatus {
  /** クリア済み（再プレイ可能） */
  CLEARED = 'cleared',
  /** 次の挑戦ステージ */
  NEXT = 'next',
  /** ロック（まだ挑戦不可） */
  LOCKED = 'locked',
}

/**
 * ステージ番号とクリア済みステージ数からステージの状態を返す
 */
export function getStageStatus(stageNumber: number, clearedStages: number): StageStatus {
  if (stageNumber <= clearedStages) return StageStatus.CLEARED;
  if (stageNumber === clearedStages + 1) return StageStatus.NEXT;
  return StageStatus.LOCKED;
}

/**
 * ステージ表示用のラベルを返す
 */
export function getStageLabel(stage: ChallengeStage): string {
  const monster = getMonsterById(stage.enemyMonsterId) ??
    [EREBOS_MONSTER, EREBOS_FORM2_MONSTER].find((m) => m.id === stage.enemyMonsterId);
  const monsterName = monster?.name ?? stage.enemyMonsterId;
  return `Stage ${stage.stageNumber} - ${monsterName}`;
}

/**
 * クリア済みステージ数に応じて表示するステージ一覧を返す
 * Stage 9はStage 8クリア後のみ表示
 */
export function getVisibleStages(clearedStages: number): ChallengeStage[] {
  return CHALLENGE_STAGES.filter((s) => s.stageNumber <= 8 || clearedStages >= 8);
}

/**
 * 表示ステージ数に応じたグリッドレイアウトを返す
 */
export function getGridLayout(stageCount: number): {
  gridRows: number;
  cellHeight: number;
  gridStartY: number;
  backButtonY: number;
} {
  if (stageCount <= 8) {
    return {
      gridRows: 2,
      cellHeight: STAGE_SELECT_LAYOUT.cellHeight,
      gridStartY: STAGE_SELECT_LAYOUT.gridStartY,
      backButtonY: STAGE_SELECT_LAYOUT.backButtonY,
    };
  }
  // 9ステージ: 3行レイアウト（圧縮）
  return {
    gridRows: 3,
    cellHeight: 130,
    gridStartY: 80,
    backButtonY: 515,
  };
}

/** ステージ選択画面のレイアウト定数 */
export const STAGE_SELECT_LAYOUT = {
  /** ヘッダーのY座標 */
  headerY: 40,
  /** グリッド列数 */
  gridCols: 4,
  /** グリッド行数 */
  gridRows: 2,
  /** グリッド開始X */
  gridStartX: 60,
  /** グリッド開始Y */
  gridStartY: 90,
  /** セル幅 */
  cellWidth: 210,
  /** セル高さ */
  cellHeight: 160,
  /** セル内パディング */
  cellPadding: 8,
  /** 戻るボタンのY座標 */
  backButtonY: 490,
} as const;

/** ステージ選択画面の色設定 */
export const STAGE_SELECT_COLORS = {
  /** クリア済みセル背景 */
  clearedBg: 0x2a6e3a,
  /** 次ステージセル背景 */
  nextBg: 0x3a5ea0,
  /** ロックセル背景 */
  lockedBg: 0x444444,
  /** セル枠線: クリア済み */
  clearedBorder: 0x44cc44,
  /** セル枠線: 次ステージ */
  nextBorder: 0x6688dd,
  /** セル枠線: ロック */
  lockedBorder: 0x666666,
  /** テキスト色 */
  text: '#ffffff',
  /** ロックテキスト色 */
  lockedText: '#888888',
  /** クリアバッジ色 */
  clearedBadge: '#44cc44',
} as const;

export { CHALLENGE_STAGES };
